<template>
  <v-card class="card-wrapper" relative>
    <v-btn icon class="btn-close" @click="onClose" :disabled="loadingCrawlData">
      <v-icon>mdi-close</v-icon>
    </v-btn>
    <h1 class="mb-4 text-capitalize primary--text">Import from other source</h1>
    <v-form ref="importForm" @submit.prevent="handleInsertSections">
      <div>
        <label class="form-label"
          >URL <span v-if="loadingCrawlData">(Analyzing URL may take some time, please wait...)</span></label
        >
        <div class="crawl-data-form__header">
          <v-text-field
            v-model="form.url"
            placeholder="https://example.com/products/your-product"
            outlined
            dense
            :disabled="loadingCrawlData"
            :rules="[v => !!v || 'URL is required!', v => /^https?:\/\/.+/.test(v) || 'Invalid URL']"
          />
          <v-btn
            class="btn-label"
            color="primary"
            :loading="loadingCrawlData"
            depressed
            min-width="140"
            :disabled="!form.url"
            @click="handleCrawlData"
          >
            Analyze URL
          </v-btn>
        </div>
      </div>

      <!-- suggestion templates -->
      <div>
        <!-- something here -->
        <div class="crawl-panel">
          <div class="crawl-panel__header crawl-panel__header--suggestion">
            <div>
              <div class="crawl-panel__label">Template suggestions</div>
              <div class="crawl-panel__title">Similar section templates</div>
            </div>
            <v-chip small color="primary" outlined>{{ (suggestedTemplates || []).length }} suggestions</v-chip>
          </div>
          <div class="crawl-suggestion-list">
            <div
              class="crawl-template-card"
              v-for="(section, index) in suggestedTemplates"
              :key="index + '-' + section.id"
            >
              <div class="crawl-template-card__top">
                <v-btn
                  :disabled="!section._crawlMeta.suggestions.length"
                  type="button"
                  color="primary"
                  depressed
                  small
                  @click="handleRollSuggestionSections(section, index)"
                  class="btn-roll"
                >
                  <v-icon size="16">mdi-reload</v-icon>
                  Roll template {{ section._crawlMeta.activeIndex }}
                </v-btn>
                <v-checkbox
                  class="select-suggestion"
                  :input-value="section.selected"
                  @change="handleSelectSuggetsTemplate(index)"
                  color="primary"
                  label="Select"
                  hide-details
                ></v-checkbox>
              </div>
              <div v-html="section.html"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="footer-action d-flex justify-end mt-2">
        <v-btn color="primary" class="text-capitalize" :disabled="loadingCrawlData" @click="onClose" text>Cancel</v-btn>
        <v-btn color="primary" class="text-capitalize" :disabled="isDisabledImport" type="submit" depressed>
          <v-icon left small>mdi-download</v-icon>
          Import
        </v-btn>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import { productApi } from '@/apis/product';
import productSectionTemplates from '@/mixins/productSectionTemplate.js';
import { getSuggestedTemplates } from './description/crawl-data/utils';
import { systemPrompt } from './description/crawl-data/promt-ai/system-prompt';
import { tools } from './description/crawl-data/promt-ai/schema-tool';
import { buildSectionByType } from './description/crawl-data/builders';
import { descriptionjs } from './description/main';

export default {
  name: 'ImportFromSource',
  mixins: [productSectionTemplates],
  data() {
    return {
      loadingCrawlData: false,
      activeInsertSections: false,
      form: {
        url: '',
      },
      suggestedTemplates: [],
      crawlRetryTimer: null,
      dataProduct: null,
    };
  },
  beforeDestroy() {
    if (this.crawlRetryTimer) {
      clearTimeout(this.crawlRetryTimer);
      this.crawlRetryTimer = null;
    }
  },
  computed: {
    isDisabledImport() {
      return this.loadingCrawlData || !this.dataProduct;
    },
  },
  methods: {
    onReset() {
      this.form = { url: '', source: 'shopify', format: 'catalog' };
      this.$refs.importForm && this.$refs.importForm.resetValidation();
    },
    onClose() {
      if (this.loadingCrawlData) return;
      this.onReset();
      this.$emit('close');
      this.suggestedTemplates = [];
      this.dataProduct = null;
    },

    async handleCrawlData() {
      const url = this.form.url.trim();

      if (!this.$refs.importForm.validate()) return;
      try {
        this.loadingCrawlData = true;
        const response = await productApi.crawlDataFromUrl({
          url,
          toolChoice: 'extract_product_page',
          systemPrompt: systemPrompt,
          tools: [tools],
        });

        if (response?.data?.status !== 'done') {
          this.crawlRetryTimer = setTimeout(() => this.handleCrawlData(url), 30000);
          return;
        }
        if (response?.data?.data?.sections && response?.data?.data?.product) {
          const product = response.data.data.product;

          // Fix image URLs - normalize protocol-relative and relative paths
          const baseUrl = new URL(url).origin;
          const fixImageUrl = src => {
            if (!src) return src;
            // If already has http/https, return as is
            if (src.startsWith('http')) return src;
            // If protocol-relative URL (starts with //), add https:
            if (src.startsWith('//')) return `https:${src}`;
            // For relative paths, only add baseUrl if not already present
            if (!src.includes(baseUrl)) return baseUrl + src;
            return src;
          };

          if (product.images && Array.isArray(product.images)) {
            product.images = product.images.map(img => ({
              ...img,
              src: fixImageUrl(img.src),
            }));
          }

          const sugges = getSuggestedTemplates(response.data.data.sections, this.sectionTemplates);
          this.$set(this, 'suggestedTemplates', sugges);
          this.$set(this, 'dataProduct', product);
          if (this.crawlRetryTimer) clearTimeout(this.crawlRetryTimer);
          this.loadingCrawlData = false;
        } else {
          if (this.crawlRetryTimer) clearTimeout(this.crawlRetryTimer);
          this.loadingCrawlData = false;
        }
      } catch (error) {
        console.error(error);
        this.loadingCrawlData = false;
      } finally {
        if (!this.crawlRetryTimer) {
          this.loadingCrawlData = false;
        }
      }
    },

    handleSelectSuggetsTemplate(index) {
      this.$set(this.suggestedTemplates, index, {
        ...this.suggestedTemplates[index],
        selected: !this.suggestedTemplates[index].selected,
      });
    },
    handleRollSuggestionSections(section, index) {
      const _crawlMeta = section._crawlMeta;
      const suggestions = _crawlMeta.suggestions;
      const initialIndex = _crawlMeta.activeIndex;
      let currentIndex = initialIndex;
      let nextSectionData = null;
      let attemptsCount = 0;

      // Retry loop: keep trying next indices until buildSectionByType returns valid data
      while (attemptsCount < suggestions.length) {
        const nextIndex = (currentIndex + 1) % suggestions.length;
        const type = suggestions[nextIndex].type;
        const suggestionFields = _crawlMeta.suggestionFields;
        const tags = _crawlMeta.suggestionTags || [];

        nextSectionData = buildSectionByType(type, this.sectionTemplates, suggestionFields, tags);

        if (nextSectionData) {
          const nextSuggestions = suggestions.map((suggestion, idx) => ({
            ...suggestion,
            active: idx === nextIndex,
          }));

          this.$set(this.suggestedTemplates, index, {
            ...nextSectionData,
            _crawlMeta: {
              ..._crawlMeta,
              activeIndex: nextIndex,
              suggestions: nextSuggestions,
            },
          });
          break;
        }

        currentIndex = nextIndex;
        attemptsCount++;
      }
    },
    handleInsertSections() {
      const selectedSections = this.suggestedTemplates.filter(item => item.selected);
      this.$emit('imported', { data: this.dataProduct, sections: selectedSections });
      this.onClose();
    },
  },
  watch: {
    suggestedTemplates: {
      deep: true,
      handler(newVal) {
        this.activeInsertSections = newVal.some(item => item.selected);
        this.$nextTick(() => {
          descriptionjs();
        });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  font-size: 20px;
}
.btn-close {
  position: absolute;
  right: 10px;
  top: 10px;
}
.form-label {
  font-weight: 500;
  font-size: 14px;
  opacity: 0.8;
}
.card-wrapper {
  min-height: fit-content;
  padding: 24px !important;
}
.footer-action {
  position: sticky;
  z-index: 9999999;
  bottom: 0px;
  left: 0;
  background-color: #ffffff;
  padding: 0px 0px 20px;
}
.btn-label {
  padding: 20px 10px !important;
}

.crawl-data-form__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.crawl-panel {
  height: 100%;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

.crawl-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.crawl-panel__header--suggestion {
  margin-bottom: 12px;
}

.crawl-panel__label {
  color: #0081c7;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.crawl-panel__title {
  margin-top: 4px;
  color: #1f2937;
  font-size: 16px;
  font-weight: 700;
}

.crawl-suggestion-list {
  display: grid;
  gap: 12px;
  max-width: 1440px;
  margin: 0 auto;
}

.crawl-template-card {
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #0081c7;
    box-shadow: 0 8px 22px rgba(16, 24, 40, 0.08);
    transform: translateY(-1px);

    .btn-roll {
      opacity: 1;
      visibility: visible;
    }
  }

  .btn-roll {
    position: absolute;
    top: 10px;
    left: 10px;
    opacity: 0;
    visibility: hidden;
    z-index: 9999;
  }

  .select-suggestion {
    position: absolute;
    top: 10px;
    right: 10px;
    margin-top: 0;
    z-index: 999;
    background: #fff;
    padding: 0 10px;
  }
}

.crawl-template-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

@media (max-width: 960px) {
  .crawl-template-card {
    display: block;
  }
  .crawl-data-form__header {
    display: block;
    .v-btn {
      width: 100%;
      margin-top: 12px;
    }
  }
}
</style>
